/**
 * React Query hooks for the Store Info page (Stores > Setup), all under
 * /v1/stores/info/* and gated by the stores.info capability:
 *
 *   GET   /v1/stores/info/stores            -> the picker list
 *   GET   /v1/stores/info/{store_no}        -> StoreDetails + network
 *   PATCH /v1/stores/info/{store_no}/network
 *   GET   /v1/stores/info/{store_no}/notes  -> newest first
 *   POST  /v1/stores/info/{store_no}/notes  -> append one
 *
 * Same conventions as useDoorTimes.ts: STORES_API_BASE for the path, no
 * retries, and a 404 or 503 means "not deployed yet" so the page shows the
 * quiet NotAvailableCard placeholder rather than an error.
 *
 * `StoreDetails` and the two cards that render it come from
 * @ampersandbrands/ui, so Cockpit and The Sweet Spot show a store the same
 * way. The details route returns that exact body plus `network`, which is
 * Cockpit-only.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { StoreDetails } from '@ampersandbrands/ui';
import { apiGet, apiPatch, apiPost } from './api';
import { STORES_API_BASE } from './useStores';

const INFO_API_BASE = `${STORES_API_BASE}/info`;

export type StoreNetworkCarrier = 'granite' | 'uniti' | 'other' | 'unknown';
export type StoreNetworkAccess = 'wired' | 'cellular' | 'unknown';

export type StoreNetwork = {
  carrier: StoreNetworkCarrier;
  access: StoreNetworkAccess;
  note: string | null;
  updated_by: string | null;
  updated_at: string | null;
};

/** The details body: the shared StoreDetails plus Cockpit's network block. */
export type StoreInfoDetails = StoreDetails & { network?: StoreNetwork | null };

export type StoreInfoPickerStore = {
  store_no: string;
  store_name: string | null;
  store_status: string | null;
};

export type StoreNote = {
  id: number;
  store_no: string;
  body: string;
  author_email: string;
  created_at: string;
};

// The picker list. One row per active store, ordered by store number by the
// API. Long staleTime: the roster changes a few times a year.
export function useStoreInfoStores(enabled: boolean): UseQueryResult<StoreInfoPickerStore[], Error> {
  return useQuery<StoreInfoPickerStore[], Error>({
    queryKey: ['stores', 'info', 'stores'],
    queryFn: async () => {
      const r = await apiGet<{ stores?: StoreInfoPickerStore[] }>(`${INFO_API_BASE}/stores`);
      return r.stores ?? [];
    },
    enabled,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

export function useStoreInfoDetails(
  storeNo: string | null,
  enabled: boolean,
): UseQueryResult<StoreInfoDetails, Error> {
  return useQuery<StoreInfoDetails, Error>({
    queryKey: ['stores', 'info', 'details', storeNo],
    queryFn: () => apiGet<StoreInfoDetails>(`${INFO_API_BASE}/${storeNo}`),
    enabled: enabled && !!storeNo,
    staleTime: 60_000,
    retry: false,
  });
}

export type StoreNetworkInput = {
  carrier: StoreNetworkCarrier;
  access: StoreNetworkAccess;
  note: string | null;
};

// Save the network box. The API stamps updated_by/updated_at, so the details
// query is invalidated rather than patched locally.
export function useSaveStoreNetwork(
  storeNo: string | null,
): UseMutationResult<StoreNetwork, Error, StoreNetworkInput> {
  const qc = useQueryClient();
  return useMutation<StoreNetwork, Error, StoreNetworkInput>({
    mutationFn: (input) => apiPatch<StoreNetwork>(`${INFO_API_BASE}/${storeNo}/network`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'info', 'details', storeNo] });
    },
  });
}

export function useStoreNotes(
  storeNo: string | null,
  enabled: boolean,
): UseQueryResult<StoreNote[], Error> {
  return useQuery<StoreNote[], Error>({
    queryKey: ['stores', 'info', 'notes', storeNo],
    queryFn: async () => {
      const r = await apiGet<{ notes?: StoreNote[] }>(`${INFO_API_BASE}/${storeNo}/notes`);
      return r.notes ?? [];
    },
    enabled: enabled && !!storeNo,
    staleTime: 30_000,
    retry: false,
  });
}

// Append one note. The log is append-only: no edit, no delete.
export function useAddStoreNote(storeNo: string | null): UseMutationResult<StoreNote, Error, string> {
  const qc = useQueryClient();
  return useMutation<StoreNote, Error, string>({
    mutationFn: (body) => apiPost<StoreNote>(`${INFO_API_BASE}/${storeNo}/notes`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'info', 'notes', storeNo] });
    },
  });
}
